import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import $ from "jquery";
import ApiURl from "../controllers/Api";
import imgLocation from "../controllers/imagePath"; // ✅ Import this

const FALLBACK_IMG = "data:image/svg+xml;utf8," + encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100">
    <rect width="100%" height="100%" fill="#f2f2f2"/>
    <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle"
      font-family="Arial" font-size="12" fill="#777">
      No Image
    </text>
  </svg>
`);

const ReviewCard = ({ review, currentUserId, isAdmin, onEdit, onDelete, onHelpful }) => {
  const [helpfulCount, setHelpfulCount] = useState(review.helpful_count || 0);
  const [userHelpful, setUserHelpful] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isHelpfulLoading, setIsHelpfulLoading] = useState(false);

  // Helper function to construct image URL exactly like products
  const getImageUrl = (filename) => {
    if (!filename) return FALLBACK_IMG;
    
    // Clean the filename
    const cleanFilename = filename.replace(/^\/+/, "");
    
    // Use imgLocation constant (same as products)
    const base = String(imgLocation || "").replace(/\/+$/, "");
    
    if (!base) return `/${cleanFilename}`;
    return `${base}/${cleanFilename}`;
  };

  const isOwnReview = currentUserId && review.user_id === parseInt(currentUserId);
  const canEdit = isOwnReview || isAdmin;
  
  const getReviewerName = () => {
    if (review.is_admin_review) {
      return review.admin_name?.trim() ? review.admin_name : "Admin";
    }
    
    if (review.reviewer_name?.trim()) {
      return review.reviewer_name;
    }
    
    if (review.user) {
      if (review.user.username) return review.user.username;
      if (review.user.firstname) {
        return review.user.firstname + (review.user.lastname ? ' ' + review.user.lastname : '');
      }
    }
    
    if (review.admin_name?.trim() && !review.is_admin_review) {
      return review.admin_name;
    }
    
    return "User";
  };

  const reviewerName = getReviewerName();

  const reviewDate = new Date(review.created_at).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const handleEdit = () => {
    onEdit(review);
  };

  const handleHelpful = async () => {
    if (!currentUserId) {
      alert("Please login to mark reviews as helpful");
      return;
    }

    setIsHelpfulLoading(true);
    try {
      const response = await $.ajax({
        url: `${ApiURl}/markHelpful.php`,
        method: "POST",
        contentType: "application/json",
        dataType: "json",
        data: JSON.stringify({
          review_id: review.id,
          user_id: currentUserId,
          is_helpful: userHelpful ? 0 : 1
        })
      });

      if (response?.success) {
        setHelpfulCount(response.helpful_count);
        setUserHelpful(!userHelpful);
        onHelpful?.(review.id, response.helpful_count);
      }
    } catch (error) {
      console.error("Error marking helpful:", error);
    } finally {
      setIsHelpfulLoading(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`text-lg ${
              star <= rating ? "text-yellow-400" : "text-gray-300"
            }`}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  const shouldTruncate = review.review_text?.length > 300 && !expanded;
  const displayText = shouldTruncate 
    ? review.review_text.substring(0, 300) + "..."
    : review.review_text;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#b29063]/20 to-[#b29063]/30 flex items-center justify-center">
            <span className="text-lg font-semibold text-[#b29063]">
              {reviewerName.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-semibold text-gray-900">{reviewerName}</h4>
              {review.is_verified_purchase === 1 && (
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                  Verified Purchase
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1">
              {renderStars(review.rating)}
              <span className="text-xs text-gray-500">{reviewDate}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        {canEdit && (
          <div className="flex gap-2">
            <button
              onClick={handleEdit}
              className="p-2 text-gray-500 hover:text-[#b29063] hover:bg-[#b29063]/10 rounded-lg transition-colors cursor-pointer"
              title="Edit review"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={() => onDelete(review)}
              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
              title="Delete review"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Review Text */}
      <div className="mb-4">
        <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
          {displayText}
        </p>
        {review.review_text?.length > 300 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-2 text-xs text-[#b29063] hover:underline font-medium cursor-pointer"
          >
            {expanded ? "Show less" : "Read more"}
          </button>
        )}
      </div>

      {/* Images - Now using imgLocation like products */}
      {review.images && review.images.length > 0 && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {review.images.slice(0, 4).map((filename, idx) => {
              const imageUrl = getImageUrl(filename);
              return (
                <button
                  key={idx}
                  onClick={() => setSelectedImage(imageUrl)}
                  className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200 hover:border-[#b29063] transition-colors cursor-pointer"
                >
                  <img
                    src={imageUrl}
                    alt={`Review ${idx + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      console.log("Image failed to load:", imageUrl);
                      e.target.onerror = null;
                      e.target.src = FALLBACK_IMG;
                    }}
                  />
                </button>
              );
            })}
            {review.images.length > 4 && (
              <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center text-xs text-gray-500">
                +{review.images.length - 4}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <button
          onClick={handleHelpful}
          disabled={isHelpfulLoading}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors cursor-pointer ${
            userHelpful
              ? "bg-[#b29063] text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          <svg
            className={`h-4 w-4 ${userHelpful ? "fill-current" : ""}`}
            fill={userHelpful ? "currentColor" : "none"}
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
            />
          </svg>
          <span>Helpful ({helpfulCount})</span>
        </button>

        {review.order_id && (
          <span className="text-xs text-gray-400">
            Order #{review.order_id}
          </span>
        )}
      </div>

      {/* Image Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/80 p-4"
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="relative max-w-3xl max-h-[90vh]"
            >
              <img
                src={selectedImage}
                alt="Review"
                className="max-w-full max-h-[90vh] object-contain rounded-lg"
                onError={(e) => {
                  console.log("Modal image failed to load:", selectedImage);
                  e.target.onerror = null;
                  e.target.src = FALLBACK_IMG;
                }}
              />
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute -top-4 -right-4 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 cursor-pointer"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ReviewCard;